import { useCallback, useState } from "react";
import { readContract, writeContract } from "@wagmi/core";
import useAccount from "@/hooks/useAccount";
import { useSwitchNetwork, useNetwork } from "wagmi";
import { toast } from "react-toastify";
import { waitForTransaction } from "wagmi/actions";
import { parseEther, parseUnits } from "viem";
import { stakingContractInfo, KATContractInfo } from "@/services/contract";
import BigNumber from "bignumber.js";
import { isUserRejectError } from "@/utils/validate";
import events from "@/services/events";
import { isSkipError } from "@/utils/validate";

export default function useStakeContract(validatorId: string) {
  const { address, balance, login } = useAccount();
  const { switchNetworkAsync } = useSwitchNetwork();
  const { chain } = useNetwork();
  const [staking, setStaking] = useState(false);
  const [unStaking, setUnStaking] = useState(false);

  const unStakeBatch = useCallback(
    async (tokenIds: string[]) => {
      if (tokenIds.length === 0) {
        return;
      }
      if (!address) {
        return login();
      }
      if (unStaking) {
        return;
      }
      setUnStaking(true);
      try {
        if (!switchNetworkAsync) {
          return;
        }
        if (chain?.id !== stakingContractInfo.chainId) {
          await switchNetworkAsync(stakingContractInfo.chainId);
        }

        const resp = await writeContract({
          ...stakingContractInfo,
          functionName: "unstakeBatch",
          args: [
            tokenIds.map((id) => {
              return Number(id);
            }),
          ],
        });
        try {
          await waitForTransaction({
            hash: resp.hash,
            confirmations: 6,
          });
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }

        events.emit("unStakeBatchSuccess", {
          validatorId,
          tokenIds,
          address,
        });
        toast.success("Un-stake success");
        return true;
      } catch (e: any) {
        if (isUserRejectError(e)) {
          return false;
        }
        console.error(e);
        toast.error(e.message);
        throw new Error(e.message);
      } finally {
        setUnStaking(false);
      }
    },
    [chain, switchNetworkAsync, unStaking, validatorId, address, login]
  );

  const stake = useCallback(
    async (amount: number) => {
      if (staking) {
        return;
      }
      if (!address) {
        return login();
      }
      setStaking(true);
      try {
        if (new BigNumber(amount).isGreaterThan(balance)) {
          throw new Error("Insufficient balance");
        }
        if (!switchNetworkAsync) {
          return;
        }
        if (chain?.id !== stakingContractInfo.chainId) {
          await switchNetworkAsync(stakingContractInfo.chainId);
        }
        const data = await readContract({
          ...KATContractInfo,
          functionName: "allowance",
          args: [address, stakingContractInfo.address],
        });
        const allowance = ((data as any) || 0n) / BigInt(1e18);
        if (allowance < BigInt(amount)) {
          const resp = await writeContract({
            ...KATContractInfo,
            functionName: "approve",
            args: [stakingContractInfo.address, parseUnits(String(amount), 18)],
          });
          try {
            await waitForTransaction({
              hash: resp.hash,
            });
          } catch (e: any) {
            if (!(await isSkipError(e))) {
              throw e;
            }
          }
        }

        const resp = await writeContract({
          ...stakingContractInfo,
          functionName: "stake",
          args: [parseEther(String(amount)), parseInt(validatorId)],
        });
        try {
          await waitForTransaction({
            hash: resp.hash,
            confirmations: 6,
          });
        } catch (e: any) {
          if (!(await isSkipError(e))) {
            throw e;
          }
        }
        events.emit("stakeSuccess", {
          validatorId,
          amount,
          address,
        });
        toast.success("Stake success");
        return true;
      } catch (e: any) {
        if (isUserRejectError(e)) {
          return;
        }
        console.error(e);
        toast.error(e.message);
        throw new Error(e.message);
      } finally {
        setStaking(false);
      }
    },
    [chain, switchNetworkAsync, staking, validatorId, balance, address, login]
  );

  return {
    stake,
    staking,
    unStakeBatch,
    unStaking,
  };
}
